(ns io.daskul.webapp.ui
  "UI components for UIx.

   Re-exports commonly used UI components with UIx-friendly wrappers.
   Currently backed by Mantine, but implementation may change."
  (:require
   [uix.core :refer [defui $]]
   [io.daskul.webapp.ui.icons :as icons]
   [io.daskul.webapp.ui.theme :as theme]
   ["@mantine/notifications" :refer [Notifications]]
   ["@mantine/hooks" :refer [useMediaQuery]]
   ["@mantine/dates" :refer [DatePickerInput DateInput TimeInput]]
   ["dayjs" :as dayjs]
   ["dayjs/plugin/customParseFormat" :as customParseFormat]
   ["@mantine/core" :refer [MantineProvider
                            Button
                            TextInput
                            NumberInput
                            Input
                            PasswordInput
                            Textarea
                            Select
                            MultiSelect
                            Switch
                            Radio
                            RadioGroup
                            Checkbox
                            CheckboxGroup
                            SegmentedControl
                            Autocomplete
                            Card
                            Table
                            Alert
                            Modal
                            Drawer
                            Loader
                            Title
                            Text
                            Stack
                            Group
                            Container
                            AppShell
                            Box
                            Anchor
                            NavLink
                            Center
                            Paper
                            ScrollArea
                            UnstyledButton
                            Badge
                            Tooltip
                            Divider
                            ActionIcon
                            Tabs
                            Menu
                            Grid
                            Burger
                            SimpleGrid
                            ThemeIcon
                            Pagination
                            RingProgress
                            Progress
                            Highlight
                            Accordion
                            Collapse
                            Popover]]))

;; Required by DateInput for custom valueFormat parsing
(.extend dayjs customParseFormat)

;; ============================================================================
;; Theme
;; ============================================================================
;;
;; The palette, type roles, density scalar and component defaults live in
;; `io.daskul.webapp.ui.theme` — one source of visual truth for both products
;; (ADR F-060). Nothing visual belongs in this namespace any more.

;; Density is a theme scalar, never per-screen (F-060). Three steps —
;; :compact | :default | :comfortable — and :default is what ships. Both
;; products share this provider, so this is the one place it is chosen.
(def ^:private density-key :default)

(def klei-theme (theme/create {:density-key density-key}))

;; Stamp the density on <html> so the token stylesheets can scope by it
;; (`[data-density="default"]` in spacing.css / typography.css). Driven by the
;; same def as the Mantine theme above, so the CSS side and the component
;; sizes cannot drift apart.
;;
;; Set at namespace load rather than in an effect: the stylesheets are parsed
;; before React mounts, and a first paint without the attribute would fall
;; back to the :root defaults and then reflow.
(defonce density-stamped
  (do (.setAttribute js/document.documentElement "data-density" (name density-key))
      true))

;; ============================================================================
;; Provider - Must wrap the app
;; ============================================================================

(defui mantine-provider [{:keys [children]}]
  ($ MantineProvider
    ;; No :cssVariablesResolver — the theme emits no custom vars now. They
    ;; come from lib/webapp-ui/css/tokens, where a media query can reach them
    ;; (#684).
    {:theme klei-theme
     :defaultColorScheme "light"}
    ($ Notifications {:position "top-right"})
    children))

;; ============================================================================
;; Layout
;; ============================================================================

(def container Container)
(def app-shell AppShell)
(def app-shell-header (.-Header AppShell))
(def app-shell-navbar (.-Navbar AppShell))
(def app-shell-main (.-Main AppShell))
(def box Box)
(def stack Stack)
(def group Group)
(def grid Grid)
(def grid-col (.-Col Grid))
(def simple-grid SimpleGrid)
(def center Center)
(def paper Paper)
(def scroll-area ScrollArea)

;; ============================================================================
;; Typography
;; ============================================================================

(def title Title)
(def text Text)

(defui multiline-text
  "Text that preserves newlines (white-space: pre-wrap).
   Use for displaying textarea field values."
  [{:keys [children] :as props}]
  ($ Text (merge (dissoc props :children)
                 {:style #js {:whiteSpace "pre-wrap"}})
    children))

(def ^:private emoji-font-family
  ;; OS color-emoji fonts, placed AHEAD of the brand font — which ships a
  ;; monochrome glyph for U+2764 that Chrome would otherwise draw for ❤️
  ;; despite the VS16 emoji selector. font-variant-emoji is the spec-correct
  ;; belt to that suspenders where the browser supports it.
  "\"Apple Color Emoji\", \"Segoe UI Emoji\", \"Noto Color Emoji\", sans-serif")

(defui emoji
  "Emoji text rendered in the OS color-emoji font, so user emoji (e.g. Messenger
   reactions) don't fall back to the brand font's monochrome glyphs. Pass :label
   for an explicit accessible name (role=img); omit it and screen readers read
   each emoji by its Unicode name."
  [{:keys [label children]}]
  ($ :span (cond-> {:style #js {:fontFamily emoji-font-family
                                :fontVariantEmoji "emoji"}}
             label (assoc :role "img" :aria-label label))
    children))

(def highlight Highlight)
(def anchor Anchor)
(def nav-link NavLink)

;; ============================================================================
;; Form elements
;; ============================================================================

(def button Button)

(defui destructive-button
  "Button for destructive actions (delete, discard, etc.).
   Defaults to red color. All standard button props pass through."
  [{:keys [children] :as props}]
  ($ Button (merge {:color "red"} (dissoc props :children))
    children))
(def text-input TextInput)
(def number-input NumberInput)
;; Input.Wrapper — the label/description/error frame Mantine puts around every
;; input, exposed on its own for composing custom controls (e.g. a narrow input
;; with an inline hint) under a standard field label.
(def input-wrapper (.-Wrapper Input))
(def password-input PasswordInput)
(def textarea Textarea)
(def select Select)
(def multi-select MultiSelect)
(def switch Switch)
(def radio Radio)
(def radio-group RadioGroup)
(def radio-card (.-Card Radio))
(def radio-indicator (.-Indicator Radio))
(def checkbox Checkbox)
(def checkbox-group CheckboxGroup)
(def segmented-control SegmentedControl)
(def autocomplete Autocomplete)
(def date-picker-input DatePickerInput)
(def date-input DateInput)
(def time-input TimeInput)
(def unstyled-button UnstyledButton)
(def action-icon ActionIcon)
(def burger Burger)

(defui table-action-icon
  "Standard action icon for table rows. Consistent size across all tables.
   Props: same as action-icon, but :size defaults to \"md\" and icon children
   should use size 18."
  [props]
  ($ ActionIcon (merge {:variant "subtle" :size "md"} props)))

;; ============================================================================
;; Data display
;; ============================================================================

(def card Card)
(def card-section (.-Section Card))
(def table Table)
(def thead (.-Thead Table))
(def tbody (.-Tbody Table))
(def tr (.-Tr Table))
(def th (.-Th Table))
(def td (.-Td Table))
(def badge Badge)
(def tooltip Tooltip)
(def theme-icon ThemeIcon)
(def ring-progress RingProgress)
(def progress Progress)
(def progress-root (.-Root Progress))
(def progress-section (.-Section Progress))
(def pagination Pagination)
(def divider Divider)
(def tabs Tabs)
(def tabs-list (.-List Tabs))
(def tabs-tab (.-Tab Tabs))
(def tabs-panel (.-Panel Tabs))
(def accordion Accordion)
(def accordion-item (.-Item Accordion))
(def accordion-control (.-Control Accordion))
(def accordion-panel (.-Panel Accordion))
(def collapse Collapse)
(def popover Popover)
(def popover-target (.-Target Popover))
(def popover-dropdown (.-Dropdown Popover))

;; ============================================================================
;; Feedback
;; ============================================================================

(def alert Alert)
(def loader Loader)

(defui centered-loader
  "Centered loading spinner with optional text label.
   Props:
   - size: loader size (e.g. \"sm\", \"md\") — default: Mantine default
   - py: vertical padding — default: \"xl\"
   - label: optional loading text"
  [{:keys [size py label]}]
  ($ Group {:justify "center" :py (or py "xl")}
    ($ Loader (cond-> {} size (assoc :size size)))
    (when label
      ($ Text {:c "dimmed" :size "sm" :mt "sm"} label))))

;; Semantic alert presets — single source of truth for alert styling.
;; Use these instead of hardcoding color/variant on ui/alert.

(defui empty-alert
  "Neutral empty-state alert for 'no records found' scenarios."
  [{:keys [title children]}]
  ($ Alert {:variant "light" :color "gray" :title (or title "No records")}
    children))

(defui info-alert
  "Informational alert — light blue tint with info icon."
  [{:keys [title children] :as props}]
  ($ Alert (merge {:variant "light" :color "blue" :title title
                   :icon ($ icons/icon-info-circle)}
                  (dissoc props :title :children))
    children))

(defui warning-alert
  "Warning alert — light yellow tint with warning icon."
  [{:keys [title children] :as props}]
  ($ Alert (merge {:variant "light" :color "yellow" :title title
                   :icon ($ icons/icon-alert-triangle)}
                  (dissoc props :title :children))
    children))

(defui error-alert
  "Error alert — light red tint with error icon."
  [{:keys [title children] :as props}]
  ($ Alert (merge {:variant "light" :color "red" :title (or title "Error")
                   :icon ($ icons/icon-alert-circle)}
                  (dissoc props :title :children))
    children))

(defui success-alert
  "Success alert — light green tint with success icon."
  [{:keys [title children] :as props}]
  ($ Alert (merge {:variant "light" :color "green" :title (or title "Success")
                   :icon ($ icons/icon-circle-check)}
                  (dissoc props :title :children))
    children))

;; ============================================================================
;; Overlays
;; ============================================================================

(def modal Modal)
(def drawer Drawer)
(def menu Menu)
(def menu-target (.-Target Menu))
(def menu-dropdown (.-Dropdown Menu))
(def menu-item (.-Item Menu))
(def menu-label (.-Label Menu))
(def menu-divider (.-Divider Menu))

;; ============================================================================
;; Hooks
;; ============================================================================

(def breakpoints
  "The Mantine breakpoint tokens, as media-query strings for `use-media-query`.
   Mirrors docs/ui/mobile-first-ui-guide.md — mobile is *below* `sm`."
  {:xs "(min-width: 36em)"
   :sm "(min-width: 48em)"
   :md "(min-width: 62em)"
   :lg "(min-width: 75em)"
   :xl "(min-width: 88em)"})

(defn use-media-query
  "Boolean subscription to a media query — for the cases where a breakpoint has
   to change *which* component renders, not just how it is styled.

   Prefer Mantine's `visibleFrom` / `hiddenFrom` props whenever CSS can do the
   job: they render one tree and let the browser decide. Reach for this only
   when both branches would otherwise mount the same subtree twice (duplicate
   effects, duplicate fetches) — e.g. content that lives in a sidebar on desktop
   and inside a Drawer on mobile.

   Reads the query synchronously on first render (`getInitialValueInEffect
   false`) so there is no mobile-layout flash on a desktop load.

   Usage: (ui/use-media-query (:md ui/breakpoints))"
  [query]
  (useMediaQuery query false #js {:getInitialValueInEffect false}))
